import type { NodeDefinition, NodeId } from "../../domain/types/index.js";

export type NodeVisualState = "done" | "current" | "open" | "locked";

export { MAP_ROW_HEIGHT, mapCanvasHeight } from "./mapDensity.js";

export type MapLayout = {
  columns: number;
  totalRows: number;
  rowOf: (node: NodeDefinition) => number;
  gridRowOf: (node: NodeDefinition) => number;
  gridColumnOf: (node: NodeDefinition) => number;
};

export function createMapLayout(nodes: readonly NodeDefinition[]): MapLayout {
  const xs = nodes.map((node) => node.x);
  const ys = nodes.map((node) => node.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const gridColumnOf = (node: NodeDefinition) => node.x - minX + 1;
  const gridRowOf = (node: NodeDefinition) => node.y - minY + 1;
  return {
    columns: Math.max(...xs) - minX + 1,
    totalRows: Math.max(...ys) - minY + 1,
    rowOf: gridRowOf,
    gridRowOf,
    gridColumnOf,
  };
}

export function overlappingPositionIds(nodes: readonly NodeDefinition[]): string[] {
  const byCell = new Map<string, string[]>();
  for (const node of nodes) {
    const key = `${node.x}:${node.y}`;
    const list = byCell.get(key) ?? [];
    list.push(node.id);
    byCell.set(key, list);
  }
  return [...byCell.values()].filter((ids) => ids.length > 1).flat();
}

const NODE_RADIUS = { x: 0.2, y: 0.34 };

export function edgeEndpoints(from: NodeDefinition, to: NodeDefinition, layout: MapLayout) {
  const fromX = layout.gridColumnOf(from) - 0.5;
  const toX = layout.gridColumnOf(to) - 0.5;
  const fromY = layout.gridRowOf(from) - 0.5;
  const toY = layout.gridRowOf(to) - 0.5;
  const dx = toX - fromX;
  const dy = toY - fromY;
  const inset = 1 / Math.hypot(dx / NODE_RADIUS.x, dy / NODE_RADIUS.y);
  return {
    x1: fromX + dx * inset,
    y1: fromY + dy * inset,
    x2: toX - dx * inset,
    y2: toY - dy * inset,
  };
}

export function nodeVisualState(
  nodeId: NodeId,
  run: { currentNodeId: NodeId; availableNodeIds: readonly NodeId[]; clearedNodeIds: readonly NodeId[] },
  upcomingIds: readonly NodeId[] = [],
): NodeVisualState {
  if (nodeId === run.currentNodeId) return "current";
  if (run.clearedNodeIds.includes(nodeId)) return "done";
  if (upcomingIds.length > 0) return upcomingIds.includes(nodeId) ? "open" : "locked";
  if (run.availableNodeIds.includes(nodeId)) return "open";
  return "locked";
}

export type MapEdge = { from: NodeId; to: NodeId };

export function collectEdges(nodes: readonly NodeDefinition[]): MapEdge[] {
  return nodes.flatMap((node) => node.next.map((to) => ({ from: node.id, to })));
}
